import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { Add, FavoriteBorderOutlined } from '@mui/icons-material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getStressTests } from '../../../../services/stresstest';
import OverviewCard from './OverviewCard';

const TEST_TYPES = ['STRESS_TEST', 'STRESS_TEST_CP'];

const parseNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string' || !value.trim()) return null;

    const parsed = Number.parseFloat(value.trim().replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
};

const parseDuration = (value) => {
    if (typeof value === 'string' && value.includes(':')) {
        const [minutes, seconds] = value.split(':').map((part) => Number(part));
        if (Number.isFinite(minutes) && Number.isFinite(seconds)) {
            return minutes + seconds / 60;
        }
    }

    return parseNumber(value);
};

const getResult = (results, keys) => {
    const key = keys.find((candidate) => results?.[candidate] != null);
    return key ? results[key] : null;
};

const normalizeStressTest = (test) => {
    const results = test?.data?.results;

    return {
        uuid: test.uuid,
        ts: test.ts,
        duration: parseDuration(getResult(results, ['Tempo'])),
        restingHeartRate: parseNumber(getResult(results, ['Repouso', 'FC repouso'])),
        peakHeartRate: parseNumber(getResult(results, ['Pico FC'])),
        recoveryHeartRate: parseNumber(
            getResult(results, ['1º min Recup', '1º min recup'])
        ),
        mets: parseNumber(getResult(results, ['METS']))
    };
};

const formatNumber = (value) =>
    Number.isInteger(value) ? String(value) : value.toFixed(1).replace('.', ',');

const formatDuration = (value) => {
    if (value == null) return '—';

    const sign = value < 0 ? '-' : '';
    const totalSeconds = Math.round(Math.abs(value) * 60);
    const minutes = Math.trunc(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return seconds === 0 ? `${sign}${minutes} min` : `${sign}${minutes} min ${seconds} s`;
};

const formatMetric = (value, unit) =>
    value == null ? '—' : `${formatNumber(value)}${unit ? ` ${unit}` : ''}`;

const formatDifference = (difference, unit, isDuration) => {
    if (difference == null) return '—';

    const sign = difference > 0 ? '+' : '';
    if (isDuration) return `${sign}${formatDuration(difference)}`;

    return `${sign}${formatNumber(difference)}${unit ? ` ${unit}` : ''}`;
};

const getDifferenceColor = (difference, improvementDirection) => {
    if (difference == null || difference === 0) return 'text.secondary';
    return difference * improvementDirection > 0 ? '#168A0D' : '#D32F2F';
};

const StressTestsOverviewCard = ({ patientUuid, onAddStressTest, onViewExams }) => {
    const { t } = useTranslation();
    const {
        data = [],
        isPending,
        isError,
        refetch
    } = useQuery({
        queryKey: ['patient-stress-tests-summary', patientUuid],
        queryFn: async () => {
            const responses = await Promise.all(
                TEST_TYPES.map((type) =>
                    getStressTests({
                        patient: patientUuid,
                        type,
                        pageLimit: 2,
                        pageToken: null,
                        qi: null,
                        qf: null
                    })
                )
            );

            return responses
                .flatMap((response) => response?.data || [])
                .sort((firstTest, secondTest) =>
                    dayjs(secondTest.ts).diff(dayjs(firstTest.ts))
                )
                .slice(0, 2)
                .map(normalizeStressTest);
        },
        enabled: Boolean(patientUuid),
        staleTime: 60 * 1000,
        refetchOnMount: 'always'
    });

    const latestTest = data[0];
    const previousTest = data[1];
    const metrics = [
        {
            key: 'duration',
            label: t('userPage.overview.stressTestSummary.duration'),
            unit: '',
            isDuration: true,
            improvementDirection: 1
        },
        {
            key: 'restingHeartRate',
            label: t('userPage.overview.stressTestSummary.restingHeartRate'),
            unit: 'bpm',
            improvementDirection: -1
        },
        {
            key: 'peakHeartRate',
            label: t('userPage.overview.stressTestSummary.peakHeartRate'),
            unit: 'bpm',
            improvementDirection: 1
        },
        {
            key: 'recoveryHeartRate',
            label: t('userPage.overview.stressTestSummary.recoveryHeartRate'),
            unit: 'bpm',
            improvementDirection: 1
        },
        {
            key: 'mets',
            label: 'METS',
            unit: '',
            improvementDirection: 1
        }
    ];

    return (
        <OverviewCard
            title={t('userPage.overview.stressTests')}
            icon={<FavoriteBorderOutlined fontSize="small" />}
            minHeight={250}
            contentInset={4}
            action={
                <Tooltip title={t('userPage.addStressTest')}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={t('userPage.addStressTest')}
                        onClick={onAddStressTest}
                    >
                        <Add />
                    </IconButton>
                </Tooltip>
            }
        >
            {isPending ? (
                <Box sx={{ display: 'grid', gap: 0.7 }}>
                    <Skeleton width={190} height={22} />
                    {[1, 2, 3, 4, 5, 6].map((item) => (
                        <Skeleton key={item} height={19} />
                    ))}
                </Box>
            ) : isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.stressTestSummary.loadError')}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => refetch()}>
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : !latestTest ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('userPage.overview.stressTestSummary.noTests')}
                    </Typography>
                </Box>
            ) : (
                <>
                    <Typography
                        sx={{ color: '#263238', fontSize: '0.8125rem', mb: 0.75 }}
                    >
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {t('userPage.overview.stressTestSummary.latestTest')}
                        </Box>{' '}
                        <Box
                            component="span"
                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                        >
                            - {dayjs(latestTest.ts).format('DD/MM/YYYY')}
                        </Box>
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                'minmax(125px, 1.25fr) repeat(3, minmax(70px, 0.8fr))',
                            columnGap: 1.5,
                            alignItems: 'center'
                        }}
                    >
                        {[
                            t('userPage.overview.stressTestSummary.parameter'),
                            t('userPage.overview.stressTestSummary.previous'),
                            t('userPage.overview.stressTestSummary.current'),
                            t('userPage.overview.stressTestSummary.difference')
                        ].map((heading) => (
                            <Typography
                                key={heading}
                                variant="caption"
                                sx={{
                                    color: '#263238',
                                    fontWeight: 700,
                                    lineHeight: 1.5
                                }}
                            >
                                {heading}
                            </Typography>
                        ))}

                        {metrics.map((metric) => {
                            const current = latestTest[metric.key];
                            const previous = previousTest?.[metric.key] ?? null;
                            const difference =
                                current != null && previous != null
                                    ? current - previous
                                    : null;
                            const formatter = metric.isDuration
                                ? formatDuration
                                : (value) => formatMetric(value, metric.unit);

                            return [
                                <Typography
                                    key={`${metric.key}-label`}
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontWeight: 700 }}
                                >
                                    {metric.label}
                                </Typography>,
                                <Typography
                                    key={`${metric.key}-previous`}
                                    variant="caption"
                                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                                >
                                    {formatter(previous)}
                                </Typography>,
                                <Typography
                                    key={`${metric.key}-current`}
                                    variant="caption"
                                    sx={{ color: '#263238', fontWeight: 700 }}
                                >
                                    {formatter(current)}
                                </Typography>,
                                <Typography
                                    key={`${metric.key}-difference`}
                                    variant="caption"
                                    sx={{
                                        color: getDifferenceColor(
                                            difference,
                                            metric.improvementDirection
                                        ),
                                        fontWeight: 700
                                    }}
                                >
                                    {formatDifference(
                                        difference,
                                        metric.unit,
                                        metric.isDuration
                                    )}
                                </Typography>
                            ];
                        })}
                    </Box>
                </>
            )}

            <Button
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={onViewExams}
                sx={{
                    alignSelf: 'flex-end',
                    mt: 'auto',
                    mr: -4,
                    px: 0.5,
                    fontWeight: 600
                }}
            >
                {t('userPage.overview.viewExams')}
            </Button>
        </OverviewCard>
    );
};

StressTestsOverviewCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onAddStressTest: PropTypes.func.isRequired,
    onViewExams: PropTypes.func.isRequired
};

export default StressTestsOverviewCard;
